// utils/profileAutoUpdater.ts
// Fixed automatic profile updater that properly replaces metadata instead of appending

import { ANT, ArconnectSigner } from "@ar.io/sdk/web";
import { generateProfilePageHTML } from './profilePageTemplate';
import { ARWEAVE_APP_NAME } from './constants';
import React from "react";

interface ProfileUpdateCheckResult {
  needsUpdate: boolean;
  reason?: string;
  lastUpdateTxId?: string;
  lastUpdateTime?: number;
  currentMetadata?: any;
}

// Check if profile needs updating
export async function checkProfileNeedsUpdate(params: {
  userData: any;
  userAntProcessId: string;
  currentPrimaryName?: string;
}): Promise<ProfileUpdateCheckResult> {
  const { userData, userAntProcessId } = params;
  
  try {
    console.log("Checking if profile needs update...");
    
    // Get current ampassport undername record
    const ant = ANT.init({ processId: userAntProcessId });
    const records = await ant.getRecords();
    const amprofileRecord = records?.amprofile;
    
    if (!amprofileRecord?.transactionId) {
      console.log("No amprofile record found - needs initial setup");
      return { needsUpdate: true, reason: "No amprofile record found" };
    }
    
    console.log("Found existing amprofile record:", amprofileRecord.transactionId);
    
    // Fetch metadata from current transaction
    try {
      const response = await fetch(`https://arweave.net/${amprofileRecord.transactionId}`);
      if (!response.ok) {
        console.warn("Could not fetch existing profile:", response.status);
        return { needsUpdate: true, reason: "Could not fetch current profile" };
      }
      
      const html = await response.text();
      
      // Extract ALL metadata comments and use the LAST one (most recent)
      const metaMatches = html.matchAll(/<!-- METADATA: (.+?) -->/g);
      const allMatches = Array.from(metaMatches);
      
      if (allMatches.length === 0) {
        console.log("No metadata found in current profile - needs update");
        return { needsUpdate: true, reason: "No metadata in current profile" };
      }
      
      // Use the LAST metadata block (most recent)
      const lastMatch = allMatches[allMatches.length - 1];
      const metadata = JSON.parse(atob(lastMatch[1]));
      console.log(`Found ${allMatches.length} metadata blocks, using the last one:`, metadata);
      
      // Calculate current level
      const currentLevel = Math.floor(userData.points / 1000);
      
      // Check if significant data has changed
      const significantChanges = {
        points: metadata.points !== userData.points,
        level: metadata.level !== currentLevel,
        xId: metadata.xId !== userData.xId,
        githubId: metadata.githubId !== userData.githubId,
        primaryName: metadata.primaryName !== params.currentPrimaryName,
        timeSinceUpdate: Date.now() - (metadata.lastUpdate || 0) > 3 * 10 * 60 * 1000
      };
      
      console.log("Change detection:", significantChanges);
      
      // Check if any significant changes occurred
      const needsUpdate = Object.values(significantChanges).some(changed => changed);
      
      if (needsUpdate) {
        const reasons = Object.entries(significantChanges)
          .filter(([_, changed]) => changed)
          .map(([key]) => key);
          
        console.log("Update needed due to:", reasons);
        
        return {
          needsUpdate: true,
          reason: `Profile data changed: ${reasons.join(', ')}`,
          lastUpdateTxId: amprofileRecord.transactionId,
          lastUpdateTime: metadata.lastUpdate,
          currentMetadata: metadata
        };
      }
      
      console.log("No update needed - profile is current");
      return { 
        needsUpdate: false,
        currentMetadata: metadata
      };
      
    } catch (error) {
      console.warn("Could not parse existing profile metadata:", error);
      return { needsUpdate: true, reason: "Could not verify current profile" };
    }
    
  } catch (error) {
    console.error("Error checking profile update status:", error);
    return { needsUpdate: false }; // Default to no update on error
  }
}

// Auto-update profile - THIS FUNCTION STAYS THE SAME
// The fix is in the template itself, not here
export async function autoUpdateProfile(params: {
  userData: any;
  currentPrimaryName?: string | null;
  currentAvatar?: string | null;
  levelTitle?: string;
  level?: number;
  userAntProcessId: string;
  version?: string;
}): Promise<{ success: boolean; message: string; txId?: string }> {
  const { userData, currentPrimaryName, currentAvatar, levelTitle, level, userAntProcessId } = params;
  
  try {
    console.log("Starting profile auto-update...");
    
    if (!window.arweaveWallet) {
      throw new Error("Wallet not connected");
    }
    
    // Generate profile HTML with current data
    // The template now handles metadata properly - no need to append
    console.log("Generating profile HTML with data:", {
      points: userData.points,
      level: level || 0,
      xId: userData.xId,
      githubId: userData.githubId
    });
    
    const profileHTML = generateProfilePageHTML({
      userId: userData.userId,
      processId: userData.processId,
      points: userData.points,
      currentPrimaryName: currentPrimaryName || undefined,
      avatarUrl: currentAvatar || undefined,
      xId: userData.xId !== "Not linked" ? userData.xId : undefined,
      githubId: userData.githubId !== "Not linked" ? userData.githubId : undefined,
      levelTitle: levelTitle,
      level: level,
      pointsBreakdown: userData.pointsStats,
      xPostingsCount: userData.xPostingsCount,
      joinDate: userData.joinDate,
      githubInteractionsCount: userData.githubInteractionsCount
    });
    
    // Verify metadata is in the HTML
    if (!profileHTML.includes("<!-- METADATA:")) {
      throw new Error("Generated HTML missing metadata");
    }
    
    // Upload to Arweave
    console.log("Uploading to Arweave...");
    const signedItem = await window.arweaveWallet.signDataItem({
      data: new TextEncoder().encode(profileHTML),
      tags: [
        { name: "Content-Type", value: "text/html" },
        { name: "App-Name", value: ARWEAVE_APP_NAME },
        { name: "Type", value: "ampassport-profile-update" },
        { name: "User-ID", value: userData.userId },
        { name: "Process-ID", value: userData.processId },
        { name: "Version", value: params.version || "2.0" },
        { name: "Auto-Update", value: "true" },
        { name: "Timestamp", value: Date.now().toString() },
        { name: "Points", value: userData.points.toString() },
        { name: "Level", value: (level || 0).toString() }
      ],
    });
    
    const uploadRes = await fetch("https://up.arweave.net/tx", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: signedItem,
    });
    
    if (!uploadRes.ok) {
      throw new Error(`Upload failed: ${uploadRes.statusText}`);
    }
    
    const { id: txId } = await uploadRes.json();
    console.log("Upload successful! Transaction ID:", txId);
    
    // Wait a bit for propagation
    console.log("Waiting for transaction to propagate...");
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    // Update the ampassport undername with new transaction
    console.log("Updating ANT record...");
    const ant = ANT.init({ 
      signer: new ArconnectSigner(window.arweaveWallet),
      processId: userAntProcessId
    });
    
    const { id: messageId } = await ant.setUndernameRecord({
      undername: "amprofile",
      transactionId: txId,
      ttlSeconds: 900 // 15 minutes
    });
    
    console.log("ANT record updated! Message ID:", messageId);
    
    return {
      success: true,
      message: "Profile auto-updated successfully!",
      txId
    };
    
  } catch (error: any) {
    console.error("Failed to auto-update profile:", error);
    return {
      success: false,
      message: error.message || "Failed to update profile"
    };
  }
}

// Hook stays the same
export function useProfileAutoUpdater(params: {
  userData: any;
  userAntProcessId: string | null;
  currentPrimaryName?: string | null;
  currentAvatar?: string | null;
  levelTitle?: string;
  level?: number;
  enabled?: boolean;
}) {
  const [checking, setChecking] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [lastCheck, setLastCheck] = React.useState<Date | null>(null);
  const [lastUpdate, setLastUpdate] = React.useState<Date | null>(null);
  const [updateStatus, setUpdateStatus] = React.useState<string>("");
  
  const checkAndUpdate = React.useCallback(async () => {
    if (!params.enabled || !params.userAntProcessId || !params.userData) {
      console.log("Auto-updater disabled or missing data");
      return { updated: false, message: "Missing requirements" };
    }
    
    setChecking(true);
    setUpdateStatus("Checking for updates...");
    
    try {
      // Check if update needed
      const checkResult = await checkProfileNeedsUpdate({
        userData: params.userData,
        userAntProcessId: params.userAntProcessId,
        currentPrimaryName: params.currentPrimaryName || undefined
      });
      
      setLastCheck(new Date());
      
      if (!checkResult.needsUpdate) {
        setUpdateStatus("Profile is up to date");
        return { updated: false, message: "Profile is up to date" };
      }
      
      // Perform update
      console.log("Update needed:", checkResult.reason);
      setUpdating(true);
      setUpdateStatus("Updating profile...");
      
      const updateResult = await autoUpdateProfile({
        userData: params.userData,
        currentPrimaryName: params.currentPrimaryName,
        currentAvatar: params.currentAvatar,
        levelTitle: params.levelTitle,
        level: params.level,
        userAntProcessId: params.userAntProcessId
      });
      
      if (updateResult.success) {
        setLastUpdate(new Date());
        setUpdateStatus(`Profile updated! TX: ${updateResult.txId?.slice(0, 8)}...`);
      } else {
        setUpdateStatus(`Update failed: ${updateResult.message}`);
      }
      
      return {
        updated: updateResult.success,
        message: updateResult.message,
        txId: updateResult.txId
      };
      
    } catch (error: any) {
      console.error("Auto-update check failed:", error);
      setUpdateStatus(`Error: ${error.message}`);
      return { updated: false, message: error.message };
    } finally {
      setChecking(false);
      setUpdating(false);
    }
  }, [params]);
  
  // Check on mount and when key data changes
  React.useEffect(() => {
    if (params.enabled && params.userAntProcessId && params.userData) {
      // Initial check after 10 seconds
      const timeout = setTimeout(() => {
        console.log("Running initial auto-update check...");
        checkAndUpdate();
      }, 10000);
      
      // Then check every hour
      const interval = setInterval(() => {
        console.log("Running periodic auto-update check...");
        checkAndUpdate();
      }, 60 * 60 * 1000); // 1 hour
      
      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }
  }, [params.userData?.points, params.userAntProcessId, params.enabled]);
  
  return {
    checking,
    updating,
    lastCheck,
    lastUpdate,
    updateStatus,
    checkAndUpdate
  };
}